// Context para Lista de Interesse com Cookies por Utilizador
import { useState, useEffect, useContext, createContext } from 'react';
import Cookies from 'js-cookie';

const InterestListContext = createContext();

export const InterestListProvider = ({ children }) => {
  const [interestList, setInterestList] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Verificar utilizador logado na inicialização
  useEffect(() => {
    const username = localStorage.getItem('username');
    if (username) {
      setCurrentUser(username);
      loadInterestListForUser(username);
    } else {
      // Para utilizadores não logados, usar lista genérica
      setCurrentUser(null);
      loadInterestListForGuest();
    }
  }, []);

  // Monitorar mudanças no localStorage (login/logout)
  useEffect(() => {
    const handleStorageChange = () => {
      const username = localStorage.getItem('username');
      if (username && username !== currentUser) {
        // Login detectado
        setCurrentUser(username);
        loadInterestListForUser(username);
      } else if (!username && currentUser) {
        // Logout detectado
        setCurrentUser(null);
        loadInterestListForGuest();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [currentUser]);

  // Carregar lista específica do utilizador
  const loadInterestListForUser = (username) => {
    try {
      const cookieName = `interestList_${username}`;
      const savedList = Cookies.get(cookieName);
      if (savedList) {
        const parsedList = JSON.parse(savedList);
        setInterestList(parsedList);
      } else {
        setInterestList({});
      }
    } catch (error) {
      console.error('Erro ao carregar lista de interesse do utilizador:', error);
      setInterestList({});
    }
  };

  // Carregar lista para utilizadores não logados
  const loadInterestListForGuest = () => {
    try {
      const cookieName = 'interestList_guest';
      const savedList = Cookies.get(cookieName);
      if (savedList) {
        const parsedList = JSON.parse(savedList);
        setInterestList(parsedList);
      } else {
        setInterestList({});
      }
    } catch (error) {
      console.error('Erro ao carregar lista de interesse de convidado:', error);
      setInterestList({});
    }
  };

  // Salvar lista específica do utilizador
  const saveInterestListForUser = (username, list) => {
    try {
      const cookieName = `interestList_${username}`;
      // Salvar por 30 dias
      Cookies.set(cookieName, JSON.stringify(list), { 
        expires: 30,
        secure: true,
        sameSite: 'strict'
      });
    } catch (error) {
      console.error('Erro ao salvar lista de interesse do utilizador:', error);
    }
  };

  // Salvar lista para utilizadores não logados
  const saveInterestListForGuest = (list) => {
    try {
      const cookieName = 'interestList_guest';
      // Salvar por 7 dias para convidados
      Cookies.set(cookieName, JSON.stringify(list), { 
        expires: 7,
        secure: true,
        sameSite: 'strict'
      });
    } catch (error) {
      console.error('Erro ao salvar lista de interesse de convidado:', error);
    }
  };

  // Salvar automaticamente quando a lista mudar
  useEffect(() => {
    if (currentUser) {
      saveInterestListForUser(currentUser, interestList);
    } else {
      // Salvar para convidados
      saveInterestListForGuest(interestList);
    }
  }, [interestList, currentUser]);

  // Função para ser chamada no login
  const onLogin = (username) => {
    setCurrentUser(username);
    loadInterestListForUser(username);
  };

  // Função para ser chamada no logout
  const onLogout = () => {
    setCurrentUser(null);
    loadInterestListForGuest();
  };

  // Adicionar produto à lista de interesse
  const addToInterestList = (productId, quantity = 1) => {
    setInterestList(prev => ({
      ...prev,
      [productId]: (prev[productId] || 0) + quantity
    }));
  };

  // Remover produto da lista
  const removeFromInterestList = (productId) => {

    setInterestList(prev => {
      const newList = { ...prev };
      delete newList[productId];
      return newList;
    });
  };

  const updateInterestQuantity = (productId, quantity) => {
    
    if (quantity <= 0) {
      removeFromInterestList(productId);
    } else {
      setInterestList(prev => ({
        ...prev,
        [productId]: quantity
      }));
    }
  };

  // Limpar lista do utilizador atual
  const clearInterestList = () => {
    if (currentUser) {
      const cookieName = `interestList_${currentUser}`;
      Cookies.remove(cookieName);
    }
    setInterestList({});
  };

  // Obter total de itens
  const getTotalItems = () => {
    return Object.values(interestList).reduce((total, quantity) => total + quantity, 0);
  };

  // Verificar se produto está na lista
  const isInInterestList = (productId) => {
    return currentUser && interestList[productId] > 0;
  };

  // Obter produtos com quantidades
  const getInterestListItems = () => {
    if (!currentUser) return [];
    
    return Object.entries(interestList)
      .filter(([_, quantity]) => quantity > 0)
      .map(([productId, quantity]) => ({
        productId: parseInt(productId),
        quantity
      }));
  };

  const value = {
    interestList,
    addToInterestList,
    removeFromInterestList,
    updateInterestQuantity,
    clearInterestList,
    getTotalItems,
    isInInterestList,
    getInterestListItems,
    isLoading,
    currentUser,
    onLogin,
    onLogout
  };

  return (
    <InterestListContext.Provider value={value}>
      {children}
    </InterestListContext.Provider>
  );
};

export const useInterestList = () => {
  const context = useContext(InterestListContext);
  if (!context) {
    throw new Error('useInterestList deve ser usado dentro de InterestListProvider');
  }
  return context;
};
